import { useCallback, useRef, useSyncExternalStore } from 'react';
import { expandRect } from '../../doc/geometry';
import { useBoard, usePaneStore } from '../context';
import { visibleWorldRect } from './camera';

/** Overscan as a fraction of the viewport on each side, so items appear before they scroll in. */
const OVERSCAN = 0.25;

/**
 * Ids of items intersecting the viewport, in z order. The returned array is referentially
 * stable while the set and order stay the same, so panning does not re-render the item list.
 */
export function useVisibleItemIds(): string[] {
  const board = useBoard();
  const { index } = board;
  const pane = usePaneStore();
  const last = useRef<{ key: string; ids: string[] }>({ key: '', ids: [] });

  const subscribe = useCallback(
    (cb: () => void) => {
      const a = index.subscribe(cb);
      const b = pane.subscribe((s, prev) => {
        if (s.camera !== prev.camera || s.size !== prev.size) cb();
      });
      return () => {
        a();
        b();
      };
    },
    [index, pane],
  );

  const getSnapshot = useCallback(() => {
    const { camera, size } = pane.getState();
    if (!size.w || !size.h) return last.current.ids;
    const rect = expandRect(visibleWorldRect(camera, size), OVERSCAN);
    const ids = index.query(rect).filter((i) => !board.isHidden(i)).map((i) => i.id);
    const key = ids.join('|');
    if (key !== last.current.key) last.current = { key, ids };
    return last.current.ids;
  }, [board, index, pane]);

  return useSyncExternalStore(subscribe, getSnapshot);
}
